(() => {
  if (window.CACustomShippingProtection) return;

  const CART_MUTATION_PATTERN =
    /\/cart\/(add|change|update|clear)(\.js)?/i;

  const CHECKOUT_SELECTOR = [
    '#MinimogCartDrawer [data-ca-checkout-cta]',
    '#MinimogCartDrawer [data-ca-bird-continue]',
    '#MinimogCartDrawer button[name="checkout"]',
    '#MinimogCartDrawer a[href="/checkout"]',
    '#MinimogCartDrawer a[href*="/checkout"]'
  ].join(',');

  const OPTOUT_ATTRIBUTE = 'ca_protection_opted_out__';
  const INTERNAL_HEADER = 'X-CA-Protection';

  let config = null;
  let queue = Promise.resolve();
  let refreshTimer = null;
  let checkoutReplay = false;

  function getRoot() {
    return document.querySelector('[data-ca-custom-protection]');
  }

  function readConfig() {
    const node = document.querySelector(
      '[data-ca-protection-config]'
    );

    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: config.currency
    }).format(cents / 100);
  }

  function planForSubtotal(cents) {
    if (cents < 100) return 0;

    return Math.min(
      99,
      Math.floor((cents - 100) / 10100) + 1
    );
  }

  function variantForPlan(plan) {
    const titlePattern = new RegExp(
      `^plan\\s*${plan}$`,
      'i'
    );

    return (
      config.variants.find((variant) =>
        titlePattern.test(variant.title)
      ) || config.variants[plan - 1]
    );
  }

  function eligibleSubtotal(cart) {
    return cart.items.reduce((subtotal, item) => {
      if (
        Number(item.product_id) ===
        Number(config.productId)
      ) {
        return subtotal;
      }

      return (
        subtotal +
        Number(item.original_line_price || 0)
      );
    }, 0);
  }

  function protectionLines(cart) {
    return cart.items.filter(
      (item) =>
        Number(item.product_id) ===
        Number(config.productId)
    );
  }

  function isOptedOut(cart) {
    return (
      String(
        (cart.attributes || {})[
          OPTOUT_ATTRIBUTE
        ] || ''
      ) === 'true'
    );
  }

  function paint({
    checked,
    price,
    status,
    busy
  }) {
    const root = getRoot();

    if (!root) return;

    const toggle = root.querySelector(
      '[data-ca-protection-toggle]'
    );

    const priceNode = root.querySelector(
      '[data-ca-protection-price]'
    );

    const statusNode = root.querySelector(
      '[data-ca-protection-status]'
    );

    if (toggle) {
      toggle.checked = checked;
      toggle.disabled = busy;
    }

    if (
      priceNode &&
      Number.isFinite(price)
    ) {
      priceNode.textContent =
        formatMoney(price);
    }

    if (statusNode) {
      statusNode.textContent = status || '';
    }
  }

  async function cartRequest(path, body) {
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [INTERNAL_HEADER]: '1'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error('Cart request failed');
    }

    return response.json();
  }

  async function fetchCart() {
    const response = await fetch(
      `/cart.js?ca_protection=${Date.now()}`,
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Cart request failed');
    }

    return response.json();
  }

  function refreshDrawer() {
    const drawer = document.querySelector(
      '#MinimogCartDrawer'
    );

    if (
      drawer &&
      typeof drawer.onCartDrawerUpdate ===
        'function'
    ) {
      drawer.onCartDrawerUpdate(true);
    }
  }

  async function synchronize(
    refreshAfterChange
  ) {
    config = readConfig() || config;

    if (
      !config ||
      !Array.isArray(config.variants) ||
      !config.variants.length
    ) {
      return;
    }

        const currentToggle = getRoot()?.querySelector(
        '[data-ca-protection-toggle]'
        );

        if (currentToggle) {
        currentToggle.disabled = true;
        }

    const cart = await fetchCart();
    const subtotal = eligibleSubtotal(cart);
    const plan = planForSubtotal(subtotal);

    const desiredVariant = plan
      ? variantForPlan(plan)
      : null;

    const existingLines =
      protectionLines(cart);

    let optedOut = isOptedOut(cart);
    let changed = false;

    if (!plan && optedOut) {
      await cartRequest('/cart/update.js', {
        attributes: {
          [OPTOUT_ATTRIBUTE]: 'false'
        }
      });

      optedOut = false;
    }

    if (!desiredVariant || optedOut) {
      for (const line of existingLines) {
        await cartRequest(
          '/cart/change.js',
          {
            id: line.key,
            quantity: 0
          }
        );

        changed = true;
      }

      paint({
        checked: false,
        price: desiredVariant
          ? Number(desiredVariant.price)
          : 0,
        status: '',
        busy: false
      });
    } else {
      const correctLine =
        existingLines.length === 1 &&
        Number(existingLines[0].variant_id) ===
          Number(desiredVariant.id) &&
        Number(existingLines[0].quantity) === 1;

      if (!correctLine) {
        for (const line of existingLines) {
          await cartRequest(
            '/cart/change.js',
            {
              id: line.key,
              quantity: 0
            }
          );
        }

        await cartRequest('/cart/add.js', {
          items: [
            {
              id: desiredVariant.id,
              quantity: 1,
              properties: {
                _ca_protection_plan:
                  String(plan)
              }
            }
          ]
        });

        changed = true;
      }

      paint({
        checked: true,
        price: Number(
          desiredVariant.price
        ),
        status: '',
        busy: false
      });
    }

    if (
      changed &&
      refreshAfterChange
    ) {
      refreshDrawer();
    }
  }

  function enqueueSync(
    refreshAfterChange = false
  ) {
    queue = queue
      .then(
        () =>
          synchronize(
            refreshAfterChange
          ),
        () =>
          synchronize(
            refreshAfterChange
          )
      )
      .catch(() => {
        paint({
          checked: false,
          price: NaN,
          status: '',
          busy: false
        });
      });

    return queue;
  }

  async function setOptOut(optedOut) {
    paint({
      checked: !optedOut,
      price: NaN,
      status: '',
      busy: true
    });

    await cartRequest('/cart/update.js', {
      attributes: {
        [OPTOUT_ATTRIBUTE]:
          optedOut ? 'true' : 'false'
      }
    });

    return enqueueSync(true);
  }

  function scheduleSync() {
    window.clearTimeout(refreshTimer);

    refreshTimer = window.setTimeout(
      () => enqueueSync(true),
      180
    );
  }

  document.addEventListener(
    'change',
    (event) => {
      if (
        !event.target.matches(
          '[data-ca-protection-toggle]'
        )
      ) {
        return;
      }

      setOptOut(
        !event.target.checked
      );
    }
  );

  document.addEventListener(
    'click',
    (event) => {
      const checkout =
        event.target.closest(
          CHECKOUT_SELECTOR
        );

      if (
        !checkout ||
        checkoutReplay
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      enqueueSync(false).finally(() => {
        checkoutReplay = true;
        checkout.click();

        window.setTimeout(() => {
          checkoutReplay = false;
        }, 600);
      });
    },
    true
  );

  const originalFetch = window.fetch;

  window.fetch = function wrappedFetch(
    input,
    options
  ) {
    const result = originalFetch.apply(
      this,
      arguments
    );

    const url =
      typeof input === 'string'
        ? input
        : (input && input.url) || '';

    const headers =
      options && options.headers;

    const internal =
      headers &&
      (
        headers[INTERNAL_HEADER] ||
        (
          typeof headers.get ===
            'function' &&
          headers.get(INTERNAL_HEADER)
        )
      );

    if (
      !internal &&
      CART_MUTATION_PATTERN.test(url)
    ) {
      result.finally(scheduleSync);
    }

    return result;
  };

  if (window.XMLHttpRequest) {
    const originalOpen =
      window.XMLHttpRequest.prototype.open;

    const originalSend =
      window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open =
      function wrappedOpen(method, url) {
        this.caProtectionUrl = url;

        return originalOpen.apply(
          this,
          arguments
        );
      };

    window.XMLHttpRequest.prototype.send =
      function wrappedSend() {
        if (
          CART_MUTATION_PATTERN.test(
            String(
              this.caProtectionUrl || ''
            )
          )
        ) {
          this.addEventListener(
            'loadend',
            scheduleSync,
            { once: true }
          );
        }

        return originalSend.apply(
          this,
          arguments
        );
      };
  }

  window.CACustomShippingProtection = {
    sync: () => enqueueSync(false)
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => enqueueSync(false),
      { once: true }
    );
  } else {
    enqueueSync(false);
  }
})();